import { useState } from 'react';
import { Building2, Loader2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { useCreateOrganization } from '@/hooks/api/useOrganizations';
import { ApiError } from '@kirin/api-client/errors';

interface CreateOrganizationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateOrganizationModal({
  open,
  onOpenChange,
}: CreateOrganizationModalProps) {
  const [name, setName] = useState('');
  const {
    mutateAsync: createOrganization,
    isPending,
    error,
  } = useCreateOrganization();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await createOrganization(name.trim());
      // Navigation and page reload are handled by the mutation
      // The mutation will reload the page to the new organization
    } catch (err) {
      // Error is already handled by the mutation and stored in the error state
      console.error('Failed to create organization:', err);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isPending) {
      // Only allow closing when not pending
      if (!newOpen) {
        setName('');
      }
      onOpenChange(newOpen);
    }
  };

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof ApiError) {
      // Handle specific error types
      if (error.status === 409) {
        return 'An organization with this name already exists.';
      }
      if (error.status === 400) {
        return 'Please provide a valid organization name.';
      }
      if (
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data
      ) {
        return String(error.data.message);
      }
    }
    return 'Failed to create organization. Please try again.';
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={!isPending}>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              <DialogTitle>Create Organization</DialogTitle>
            </div>
            <DialogDescription>
              Create a new organization to manage team policies and settings.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter organization name"
                disabled={isPending}
                autoFocus
                maxLength={254}
                required
              />
            </div>

            {error && (
              <HighlightBox
                variant="error"
                radius="md"
                className="flex items-start gap-2 text-sm"
                role="alert"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{getErrorMessage(error)}</span>
              </HighlightBox>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name.trim()}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Organization'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
